import React, { useState, useEffect } from 'react';
import {
    FormLabel,
    Button,
    TextInput,
    FormGroup,
    Checkbox,
    InlineNotification,
    Link,
    TextArea,
    MultiSelect
} from '@carbon/react';
import { Column, Row } from "./Layout";
import { spacing } from "../constants/styles.constants";
import { storyCategories } from "../constants/form.constants";
import endpoints from "../constants/endpoints.constants";
import Loading from "./Loading";
import style from "./StoryDetails.module.scss";

// eslint-disable-next-line
export default (props) => {
    const { storyData, createdStory } = props;
    const isEdit = Object.keys(storyData).length > 0;
    const [formData, setFormData] = useState({
        videoTitle: '',
        embedLink: '',
        videoDescription: '',
        videoCategories: '',
        agreedToTermsAndConditions: false,
        isFilmFestival: false,
    });

    // Parse categories for display/editing if they come as comma-separated string
    // The Input field will treat them as comma separated string for simplicity or we can use MultiSelect if we have a list of categories
    // For now, based on "videoCategories: DataTypes.STRING", I'll use a simple TextInput or TextArea if freeform, 
    // or if it matches StoryCategories we should use that. 
    // The user didn't specify a restricted list for YoutubeVideo categories, but "storyCategories" in StoryDetails used a defined list.
    // I will use a TextInput for now as the model getter/setter hints it's just a comma separated list.

    useEffect(() => {
        if (isEdit) {
            setFormData({
                videoTitle: storyData.videoTitle || '',
                embedLink: storyData.embedLink || '',
                videoDescription: storyData.videoDescription || '',
                videoCategories: storyData.videoCategories || [], // getter returns array
                isFilmFestival: storyData.isFilmFestival || false,
                agreedToTermsAndConditions: false,
            })
        }
    }, [storyData, isEdit])

    const isFormDisabled = (props.isLoading || props.isSuccess);

    return (
        <>
            <Row space={spacing.medium}>
                <Column>
                    <h3 className={style.bottomPadding}>Youtube Story</h3>
                    <p>Enter details about the Youtube Video</p>
                </Column>
            </Row>
            <Row space={spacing.small}>
                <Column>
                    <Loading
                        isLoading={props.isLoading}
                        isSuccess={props.isSuccess}
                        isError={props.isError}
                        errorMessage={props.errorMessage}
                    />
                </Column>
            </Row>

            <Row space={spacing.small}>
                <Column>
                    <FormGroup legendText={''}>
                        <TextInput
                            helperText="Set a title for your Video"
                            id="videoTitle"
                            labelText="Video Title"
                            placeholder="Video Title"
                            value={formData.videoTitle}
                            disabled={isFormDisabled}
                            onChange={(event) => setFormData({
                                ...formData,
                                videoTitle: event.target.value
                            })}
                        />
                    </FormGroup>
                </Column>
            </Row>

            <Row space={spacing.small}>
                <Column>
                    <FormGroup legendText={''}>
                        <TextInput
                            id="embedLink"
                            labelText="Youtube Embed Link"
                            helperText="e.g. https://www.youtube.com/embed/..."
                            placeholder="https://www.youtube.com/embed/..."
                            value={formData.embedLink}
                            disabled={isFormDisabled}
                            onChange={(event) => setFormData({
                                ...formData,
                                embedLink: event.target.value
                            })}
                        />
                    </FormGroup>
                </Column>
            </Row>

            {formData.embedLink && (
                <Row space={spacing.small}>
                    <Column>
                        <iframe
                            id="player"
                            type="text/html"
                            width="400"
                            height="250"
                            title='Youtube video'
                            src={formData.embedLink}
                            frameBorder="0"
                        />
                    </Column>
                </Row>
            )}

            <Row space={spacing.small}>
                <Column>
                    <FormGroup legendText={''}>
                        <TextArea
                            helperText="Description of the video"
                            id="videoDescription"
                            labelText="Video Description"
                            placeholder="Video Description"
                            disabled={isFormDisabled}
                            value={formData.videoDescription}
                            onChange={(event) => setFormData({
                                ...formData,
                                videoDescription: event.target.value
                            })}
                        />
                    </FormGroup>
                </Column>
            </Row>

            <Row space={spacing.small}>
                <Column>
                    <FormGroup legendText={''}>
                        <MultiSelect
                            id="video-category-selector"
                            ariaLabel="Category Selector"
                            items={storyCategories}
                            initialSelectedItems={formData.videoCategories ? formData.videoCategories.map(
                                category => storyCategories.find(dbCategory => dbCategory.databaseId === parseInt(category))
                            ).filter(Boolean) : []}
                            label="Select Categories"
                            titleText="Select Your Categories"
                            disabled={isFormDisabled}
                            onChange={(event) =>
                                setFormData({
                                    ...formData,
                                    videoCategories: event.selectedItems.map(story => story.databaseId.toString()),
                                })
                            }
                        />
                    </FormGroup>
                </Column>
            </Row>

            <Row space={spacing.medium}>
                <Column>
                    <Checkbox
                        defaultChecked={formData.isFilmFestival}
                        labelText={
                            <span>I want to enter this Story into the Film Festival and Agree to the <a target="_blank"
                                rel="noopener noreferrer"
                                href={endpoints.termsAndConditionsFilmFestival()}>
                                Terms and Conditions
                            </a>
                            </span>
                        }
                        id="terms-conditions-film-festival"
                        disabled={isFormDisabled}

                        onClick={(event) => {
                            setFormData({
                                ...formData,
                                isFilmFestival: event.target.checked,
                            })
                        }}
                    />
                </Column>
            </Row>
            <Row space={spacing.medium}>
                <Column>
                    <Checkbox
                        defaultChecked={false}
                        labelText={
                            <span>I Agree to the <a target="_blank"
                                rel="noopener noreferrer"
                                href={endpoints.termsAndConditions()}>
                                Terms and Conditions
                            </a>
                            </span>
                        }
                        id="terms-conditions"
                        value={formData.agreedToTermsAndConditions}
                        disabled={isFormDisabled}
                        onChange={(event) => setFormData({
                            ...formData,
                            agreedToTermsAndConditions: event.target.checked
                        })}
                    />
                </Column>
            </Row>
            <Row className={style.termsAndConditions} space={spacing.small}>
                <Column>
                    <FormLabel>
                        Yesterday Stories Pty Ltd reserves the right not to publish any video or content including
                        but not limited to low sound and video quality, disputable history, stories which cause offence
                        or not aligned with community standards and expectation. Please see the &nbsp;
                        <a rel="noopener noreferrer" target="_blank"
                            href='https://media.yesterdaystories.net.au/docs/terms-and-conditions.pdf'>
                            Terms and Conditions
                        </a>
                        &nbsp;for more details.
                    </FormLabel>
                </Column>
            </Row>

            <Row space={spacing.small}>
                <Column>
                    <Loading
                        isLoading={props.isLoading}
                        isSuccess={props.isSuccess}
                        isError={props.isError}
                        errorMessage={props.errorMessage}
                    />
                </Column>
            </Row>

            <Row space={spacing.small}>
                <Column>
                    {(createdStory && !isEdit) && (
                        <>
                            <InlineNotification
                                kind={'success'}
                                title="Youtube Story Created"
                                hideCloseButton={true}
                                subtitle={(
                                    <>
                                        <Link href={endpoints.listYoutubeStories()}>Go back to the list</Link>
                                    </>
                                )}
                            />
                            <br />
                            <Button kind="secondary" onClick={() => {
                                setFormData({
                                    videoTitle: '',
                                    embedLink: '',
                                    videoDescription: '',
                                    videoCategories: '',
                                    publishDate: '',
                                    isApprovedAds: false,
                                    isApproved: false,
                                });
                                props.onReset && props.onReset();
                            }}>
                                Create Another
                            </Button>
                        </>
                    )}
                </Column>
            </Row>

            <Row space={spacing.large}>
                <Column>
                    {!createdStory && <Button
                        kind="primary"
                        tabIndex={0}
                        disabled={props.isLoading || props.isSuccess}
                        onClick={() => {
                            if (!formData.isFilmFestival) {
                                formData.isFilmFestival = false
                            }

                            if (!formData.agreedToTermsAndConditions && !isEdit) {
                                alert("Please agree to terms and conditions")
                                return;
                            }

                            props.submit({
                                ...formData,
                            }, isEdit)
                        }}
                    >
                        Save Youtube Story
                    </Button>}
                </Column>
            </Row>
        </>
    )
}
